import { useState, useEffect, useCallback } from "react";
import { Box, Typography, IconButton, Tooltip, MenuItem, Menu, Badge, CircularProgress, Button, Toolbar } from "@mui/material";
import { DataGridPro, ColumnsPanelTrigger } from "@mui/x-data-grid-pro";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import DnsIcon from "@mui/icons-material/Dns";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { dataGridStyle, styles } from "../../../../theme";
import LogSidePanel from "../../../../components/LogSidePanel";
import API from "../../../../api";

dayjs.extend(utc);
dayjs.extend(timezone);

const SEVERITY_LABEL = { 0: "High", 1: "Critical", 2: "Alert", 3: "Movements" };

const THREAT_KEYWORDS = ["malware", "metasploit", "zerologon", "eternalblue", "mirai", "cobalt", "wannacry", "reverse shell"];
const SUSPICIOUS_KEYWORDS = ["scan", "mining", "powershell", "pivot", "shellcode"];
const NOTABLE_KEYWORDS = ["trojan", "system32", "hkey", "cve", "exploit"];

const detailColor = (detail) => {
  const lower = (detail || "").toLowerCase();
  if (THREAT_KEYWORDS.some((k) => lower.includes(k))) return "#DB4F4A";
  if (SUSPICIOUS_KEYWORDS.some((k) => lower.includes(k))) return "#C9B85E";
  if (NOTABLE_KEYWORDS.some((k) => lower.includes(k))) return "#F57F22";
  return "inherit";
};

const convertToCSV = (data, dnsOrNetbios, timeZone, multiSecurityView) => {
  if (!data || data.length === 0) return "";
  const headers = multiSecurityView
    ? ["Timestamp", "Source IP", "Destination IP", "Source MAC", "Destination MAC", "Severity", "Probe Name", "Details"]
    : ["Timestamp", "Source IP", "Destination IP", "Source MAC", "Destination MAC", "Severity", "Details"];
  const rows = [headers.join(",")];

  const nameFor = (row, side) => {
    const key = { dns: "dnsName", netbios: "netBiosName", custom: "customName" }[dnsOrNetbios];
    const suffix = side === "src" ? "Src" : "Dest";
    return (key && row[`${key}${suffix}`]) || (side === "src" ? row.ipSource : row.ipDestination);
  };

  data.forEach((row) => {
    const values = [
      row.timeStamp ? dayjs(row.timeStamp).tz(timeZone).format("DD/MM/YYYY HH:mm:ss") : "",
      nameFor(row, "src") || "",
      nameFor(row, "dest") || "",
      row.macSource || "",
      row.macDestination || "",
      row.tipo != null ? SEVERITY_LABEL[row.tipo] || `Type ${row.tipo}` : "",
      ...(multiSecurityView ? [row.probeName || ""] : []),
      row.dettaglio || "",
    ];
    rows.push(values.map((v) => `"${v}"`).join(","));
  });

  return rows.join("\n");
};

const downloadFile = (content, filename, contentType) => {
  const blob = new Blob([content], { type: contentType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const CustomToolbar = ({ handleExport, exportLoading, setDnsOrNetbios, dnsOrNetbios }) => {
  const [anchorElExport, setAnchorElExport] = useState(null);
  const [anchorShowName, setAnchorShowName] = useState(null);

  return (
    <Toolbar sx={{ display: "flex", justifyContent: "flex-start", backgroundColor: "#459296" }}>
      <ColumnsPanelTrigger>
        <DnsIcon /> COLUMNS
      </ColumnsPanelTrigger>

      <div>
        <Button color="primary" size="small" onClick={(e) => setAnchorElExport(e.currentTarget)} disabled={exportLoading}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {exportLoading ? <CircularProgress size={22} /> : <SaveAltIcon fontSize="small" />} EXPORT
          </div>
        </Button>
        <Menu anchorEl={anchorElExport} open={Boolean(anchorElExport)} onClose={() => setAnchorElExport(null)}>
          <MenuItem onClick={() => { setAnchorElExport(null); handleExport("csv"); }}>Export CSV</MenuItem>
          <MenuItem onClick={() => { setAnchorElExport(null); handleExport("json"); }}>Export JSON</MenuItem>
        </Menu>
      </div>

      <div>
        <Badge
          badgeContent={dnsOrNetbios?.toUpperCase()}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          sx={{ "& .MuiBadge-badge": { top: 5, left: 5, minWidth: 30, padding: "0 6px", fontSize: 10, bgcolor: "#dd8d16" } }}
        >
          <Button color="primary" size="small" onClick={(e) => setAnchorShowName(e.currentTarget)}>
            <DnsIcon style={{ marginRight: 8 }} />
            Show Name
          </Button>
        </Badge>
        <Menu anchorEl={anchorShowName} open={Boolean(anchorShowName)} onClose={() => setAnchorShowName(null)}>
          <MenuItem onClick={() => { setDnsOrNetbios("dns"); setAnchorShowName(null); }}>DNS Name</MenuItem>
          <MenuItem onClick={() => { setDnsOrNetbios("netbios"); setAnchorShowName(null); }}>NetBIOS Name</MenuItem>
          <MenuItem onClick={() => { setDnsOrNetbios("custom"); setAnchorShowName(null); }}>Custom Name</MenuItem>
        </Menu>
      </div>
    </Toolbar>
  );
};

// Log-level detail grid for one source↔destination pair — ported from the
// removed "Search by IP" page's detailsTable.jsx. Shown inside PairDetailDialog
// alongside the port-to-port graph.
const PairDetailTable = ({ pair, dateFilter, multiSecurityView = false }) => {
  const [dataDetails, setDataDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });
  const [totalLogs, setTotalLogs] = useState(0);
  const [timeZone, setTimeZone] = useState("GMT");
  const [dnsOrNetbios, setDnsOrNetbios] = useState("dns");
  const [exportLoading, setExportLoading] = useState(false);
  const [logPanelRow, setLogPanelRow] = useState(null);

  useEffect(() => {
    setTimeZone(window.sessionStorage.getItem("timezone") || "GMT");
  }, []);

  const loadPairData = useCallback(async () => {
    if (!pair?.ipSource || !pair?.ipDest) return;
    setLoading(true);
    try {
      const apiCall = multiSecurityView ? API.getDetailCoupleAllLecs : API.getDetailCouple;
      const response = await apiCall({
        main: pair.ipSource,
        ip: pair.ipDest,
        dateFrom: dateFilter?.dateFrom?.toISOString?.() ?? dateFilter?.dateFrom,
        dateTo: dateFilter?.dateTo?.toISOString?.() ?? dateFilter?.dateTo,
        pageNumber: paginationModel.page,
        limit: paginationModel.pageSize,
        dnsOrNetbios,
      });
      setDataDetails((response.data.data || []).map((item, index) => ({ id: index, ...item })));
      setTotalLogs(response.data.totalLogs || 0);
    } catch (error) {
      console.error("[PairDetailTable] Error loading pair data:", error);
      setDataDetails([]);
      setTotalLogs(0);
    } finally {
      setLoading(false);
    }
  }, [pair?.ipSource, pair?.ipDest, dateFilter, paginationModel.page, paginationModel.pageSize, dnsOrNetbios, multiSecurityView]);

  useEffect(() => { loadPairData(); }, [loadPairData]);

  const handlePaginationModelChange = (e) => {
    if (e.page > paginationModel.page) setPaginationModel({ ...paginationModel, page: paginationModel.page + 1 });
    else if (e.page < paginationModel.page) setPaginationModel({ ...paginationModel, page: paginationModel.page - 1 });
  };

  const handleExport = (format) => {
    setExportLoading(true);
    try {
      if (format === "csv") {
        downloadFile(convertToCSV(dataDetails, dnsOrNetbios, timeZone, multiSecurityView), "pair_details.csv", "text/csv");
      } else {
        downloadFile(JSON.stringify(dataDetails, null, 2), "pair_details.json", "application/json");
      }
    } catch (error) {
      console.error(`[PairDetailTable] Error exporting ${format}:`, error);
    } finally {
      setExportLoading(false);
    }
  };

  const nameField = { dns: "dnsName", netbios: "netBiosName", custom: "customName" }[dnsOrNetbios];

  const columns = [
    {
      field: "__info",
      headerName: "",
      width: 46,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      hideable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Tooltip title="View log details">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setLogPanelRow(params.row); }} sx={{ color: "#aaa" }}>
            <InfoOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "timeStamp",
      headerName: `TIMESTAMP (${timeZone})`,
      minWidth: 180,
      headerAlign: "left",
      align: "left",
      filterable: false,
      renderCell: (params) => (params.value ? dayjs(params.value).tz(timeZone).format("DD/MM/YYYY HH:mm:ss") : "-"),
    },
    {
      field: "ipSource",
      headerName: "SOURCE",
      minWidth: 230,
      headerAlign: "left",
      filterable: false,
      flex: 1,
      renderCell: (params) => {
        if (!params.row?.ipSource) return "-";
        const name = nameField && params.row[`${nameField}Src`];
        const fallback = `${params.value}${params.row.SourcePort ? `:${params.row.SourcePort}` : ""}`;
        return <span>{name || fallback}</span>;
      },
    },
    {
      field: "ipDestination",
      headerName: "DESTINATION",
      minWidth: 230,
      headerAlign: "left",
      filterable: false,
      flex: 1,
      renderCell: (params) => {
        if (!params.row?.ipDestination) return "-";
        const name = nameField && params.row[`${nameField}Dest`];
        const fallback = `${params.value}${params.row.DestinationPort ? `:${params.row.DestinationPort}` : ""}`;
        return <span>{name || fallback}</span>;
      },
    },
    { field: "MacSource", headerName: "SOURCE MAC", minWidth: 120, headerAlign: "left", filterable: false, renderCell: (p) => p.row?.MacSource || "-" },
    { field: "MacDestination", headerName: "DESTINATION MAC", minWidth: 120, headerAlign: "left", filterable: false, renderCell: (p) => p.row?.MacDestination || "-" },
    {
      field: "tipo",
      headerName: "SEVERITY",
      minWidth: 120,
      headerAlign: "left",
      align: "left",
      filterable: false,
      renderCell: (params) => {
        const tipo = params.row?.tipo;
        if (tipo == null) return "-";
        const label = SEVERITY_LABEL[tipo] || `Type ${tipo}`;
        return <span style={{ color: styles.logColors[tipo] || "inherit", fontWeight: "bold" }}>{label}</span>;
      },
    },
    ...(multiSecurityView ? [{
      field: "probeName",
      headerName: "PROBE NAME",
      minWidth: 150,
      headerAlign: "left",
      align: "left",
      filterable: true,
      renderCell: (params) => params.row.probeName || "-",
    }] : []),
    {
      field: "dettaglio",
      headerName: "DETAILS",
      flex: 1,
      minWidth: 280,
      headerAlign: "left",
      align: "left",
      filterable: false,
      renderCell: (params) => {
        if (!params.row?.dettaglio) return "-";
        return (
          <span style={{ color: detailColor(params.row.dettaglio), whiteSpace: "normal", wordBreak: "break-word" }}>
            {params.row.dettaglio}
          </span>
        );
      },
    },
  ];

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#459296", mb: 1 }}>
        {pair?.ipSource} ↔ {pair?.ipDest}
      </Typography>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <DataGridPro
          sx={dataGridStyle}
          rows={dataDetails}
          getRowId={(row) => row.id}
          localeText={{ noRowsLabel: !loading && dataDetails.length === 0 ? "No data in the set dates" : "" }}
          getRowHeight={() => "auto"}
          pagination
          paginationMode="server"
          columns={columns}
          pageSizeOptions={[100]}
          rowCount={totalLogs}
          onPaginationModelChange={handlePaginationModelChange}
          paginationModel={paginationModel}
          loading={loading}
          slots={{ toolbar: () => <CustomToolbar handleExport={handleExport} exportLoading={exportLoading} setDnsOrNetbios={setDnsOrNetbios} dnsOrNetbios={dnsOrNetbios} /> }}
          slotProps={{ loadingOverlay: { variant: "skeleton" } }}
          showToolbar
        />
      </Box>
      <LogSidePanel log={logPanelRow} open={Boolean(logPanelRow)} onClose={() => setLogPanelRow(null)} />
    </Box>
  );
};

export default PairDetailTable;
